import * as React from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';

import { media } from 'styles/utils';

import Logo from 'images/sleuth-logo.svg?svgr';

import Container from 'components/Container';
import Stack from 'components/Stack';
import Text from 'components/Text';
import Row from 'components/Row';
import IconMetrics from 'images/editorial-graphics/icon-secure-stack.svg?svgr';
import IconSharedUnderstanding from 'images/editorial-graphics/icon-stars.svg?svgr';
import IconOutcomes from 'images/editorial-graphics/icon-metric-growth.svg?svgr';

const Section = styled.section`
  padding: 4.25rem 1rem;

  a {
    color: inherit;
  }
`;

const StyledRow = styled(Row)`
  list-style-type: '';
`;

const StyledFeature = styled(Feature)`
  -webkit-font-smoothing: antialiased;
  background-image: radial-gradient(
      circle at center -50%,
      rgb(51 122 183 / 25%) 15%,
      rgb(51 122 183 / 0%) 75%
    ),
    linear-gradient(rgb(29 41 57 / 60%) 35%, rgb(16 24 40 / 60%) 100%);
  border-radius: 1rem;
  border: 0.125rem solid #344054;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-inline-size: 28rem;
  padding: 1.5rem 2rem 2rem;
`;

const Icon = styled('span')`
  background-color: rgb(235 245 250 / 5%);
  border-radius: 100%;
  display: flex;
  padding: 1.25rem;
  width: fit-content;

  svg {
    height: 2.5rem;
    overflow: visible;
    width: 2.5rem;
  }
`;

const items = [
  {
    icon: <IconMetrics />,
    title: 'Step 1: Surface the right metrics',
    content:
      'Sleuth’s powerful data tools surface key metrics for every level of engineering.',
  },
  {
    icon: <IconSharedUnderstanding />,
    title: 'Step 2: Build a shared interpretation',
    content:
      'Sleuth’s Review workflow, collaboration tools and native AI transforms data into meaningful, shared insights.',
  },
  {
    icon: <IconOutcomes />,
    title: 'Step 3: Drive changed outcomes',
    content:
      'Sleuth connects outcomes, goals, and tasks to data, driving meaningful change.',
  },
];

const Figure = styled.figure`
  align-items: center;
  display: flex;
  flex-direction: column;

  svg * {
    fill: currentColor;
  }

  ${media.min('md')} {
    .section-image-sm {
      display: none !important;
    }
    .section-image-lg {
      display: block !important;
    }
  }
`;

const BracketGraphic = styled.svg`
  aspect-ratio: 7.5;
  block-size: auto;
  grid-column: 2;
  inline-size: min(calc(100% - 3rem), 54rem);
  margin-block: 1rem;
  margin-inline: auto;
  mask-image: linear-gradient(to top, #0000 0.5rem, #0008 40%, #000a);
  min-block-size: 4.5rem;
  overflow: visible;
  stroke-width: 0.125rem;

  ${media.min('md')} {
    grid-column: 1 / -1;
    grid-row: 3;
    stroke-width: 0.25rem;
  }

  ${media.max('md')} {
    margin-block-start: calc(-1 * var(--row-gap, 0));
  }

  path {
    fill: none;
    stroke: #fff;
    vector-effect: non-scaling-stroke;
  }
`;

export default function DataSourcesFeatureSection() {
  return (
    <Section>
      <Container as={Stack} gap="2.625rem" size="md">
        <Text as="h2" variant="heading.section">
          How we drive change with your Metrics
        </Text>
        <Text variant="md">
          Sleuth handles the complex work of gathering key data from your tools
          and unifying information across sources. With built-in workflows and
          automation, it enables seamless collaboration for a shared
          understanding of your data and equips you with the tools to drive
          impactful outcomes.{' '}
        </Text>
        <StyledRow breakpoint="lg" as="ul" gap="1.25rem">
          {items.map((item) => (
            <StyledFeature key={item.title} {...item} />
          ))}
        </StyledRow>

        <Figure>
          <Logo />
          <BracketGraphic
            width="240"
            height="60"
            viewBox="0 0 240 60"
            preserveAspectRatio="none"
          >
            <path d="M 4 60 C 4 60 8 52 28 48 L 88 36 C 88 36 123 29 124 0" />
            <path d="M 236 60 C 236 60 232 52 212 48 L 152 36 C 152 36 117 29 116 0" />
          </BracketGraphic>
          <StaticImage
            alt=""
            className="section-image-sm"
            quality={100}
            src="../../images/editorial-graphics/illustration-destination-services-sm.webp"
            style={{ display: 'block' }}
          />
          <StaticImage
            alt=""
            className="section-image-lg"
            quality={100}
            src="../../images/editorial-graphics/illustration-destination-services-lg.webp"
            style={{ display: 'none' }}
            width={883}
          />
        </Figure>
      </Container>
    </Section>
  );
}
function Feature({ className, content, icon, title }) {
  return (
    <li className={className}>
      <Icon>{icon}</Icon>
      <Text variant="heading.feature.lg">{title}</Text>
      <Text variant="smallestP">{content}</Text>
    </li>
  );
}
